import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.ts';
import { notificationsApi } from '../features/notifications/api.ts';
import { Icon } from './Icon.tsx';
import { dv } from '../i18n/dv.ts';

type NavItem = { to: string; label: string; icon: string; matchPrefix?: string; showUnread?: boolean };

export function BottomNav(): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: notifs } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationsApi.list(),
    enabled: !!user,
  });
  const unread = notifs?.items.filter((i) => !i.isRead).length ?? 0;

  if (!user) return null;

  const items: NavItem[] = user.role === 'SYS_ADMIN'
    ? [
        { to: '/admin/orgs', label: dv.nav.orgs, icon: 'apartment', matchPrefix: '/admin' },
        { to: '/notifications', label: dv.nav.notifications, icon: 'notifications', showUnread: true },
        { to: '/profile', label: dv.nav.profile, icon: 'person' },
      ]
    : [
        { to: '/', label: dv.nav.dashboard, icon: 'dashboard' },
        { to: '/meetings', label: dv.nav.meetings, icon: 'event' },
        { to: '/notifications', label: dv.nav.notifications, icon: 'notifications', showUnread: true },
        { to: '/statistics', label: dv.nav.statistics, icon: 'bar_chart', matchPrefix: '/statistics' },
        { to: '/thanfeezu', label: dv.nav.thanfeezu, icon: 'fact_check', matchPrefix: '/thanfeezu' },
        { to: '/profile', label: dv.nav.profile, icon: 'person' },
      ];

  return (
    <nav
      aria-label={dv.nav.dashboard}
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[hsl(var(--chrome))]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <ul className="flex items-stretch">
        {items.map((item) => {
          const active = item.matchPrefix ? pathname.startsWith(item.matchPrefix) : pathname === item.to;
          const showBadge = item.showUnread && unread > 0;
          return (
            <li key={item.to} className="flex-1">
              <Link
                to={item.to}
                aria-label={item.label}
                aria-current={active ? 'page' : undefined}
                className="flex h-14 w-full flex-col items-center justify-center gap-1 active:scale-95 transition-transform"
              >
                <span className="relative inline-flex items-center justify-center">
                  <span
                    className={[
                      'inline-flex items-center justify-center rounded-full px-4 py-0.5 transition-colors',
                      active ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]' : 'text-muted-foreground',
                    ].join(' ')}
                  >
                    <Icon name={item.icon} size={22} fill={active} />
                  </span>
                  {showBadge && (
                    <span
                      aria-label={`${unread} ${dv.notifications.title}`}
                      className="absolute -top-0.5 end-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[9px] font-semibold text-destructive-foreground tabular"
                    >
                      {unread > 9 ? '9+' : unread}
                    </span>
                  )}
                </span>
                <span className={active ? 'text-[11px] leading-none text-ink font-medium' : 'text-[10.5px] leading-none text-muted-foreground'}>
                  {item.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
